import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from '@/components/Button';
import SearchInput from '@/components/SearchInput';
import { useMediaQuery, useShallowEqualSelector } from '@/hooks';
import {
	useHasActiveSubscription,
	useIsCollaborator,
	useIsExtendedCollaborator
} from '@/hooks/user';
import CollaboratorsModal from '@/pages/Collaborators/CollaboratosModal';
import DeleteCollaboratorModal from '@/pages/Collaborators/DeleteCollaboratorModal';
import UpgradeToAddUsersModal from '@/pages/Collaborators/UpgradeToAddUsersModal';
import routes from '@/routes';
import {
	setCreateCollaboratorModal,
	setSearchTerm,
	setUpgradeToAddUsersModal
} from '@/store/collaborators';
import { fetchCollaborators } from '@/store/collaborators/thunks';
import { fetchFolders } from '@/store/myDocuments/thunks';
import { showError } from '@/utils';

import Table from './UsersTable/Table';

function Collaborators() {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const hasActiveSubscription = useHasActiveSubscription();
	const mdDown = useMediaQuery('mdDown');
	const isCollaborator = useIsCollaborator();
	const isExtendedCollaborator = useIsExtendedCollaborator();

	const { data, searchTerm } = useShallowEqualSelector(
		({ collaborators }) => ({
			data: collaborators.collaborators,
			searchTerm: collaborators.searchTerm
		})
	);

	const [collaboratorsList, setCollaboratorsList] = useState(data);

	useEffect(() => {
		if (isCollaborator || isExtendedCollaborator) {
			navigate(routes.home);
		}
	}, [isCollaborator, isExtendedCollaborator]);

	useEffect(() => {
		if (searchTerm) {
			setCollaboratorsList(data.filter(c => c.email.includes(searchTerm)));
		} else {
			setCollaboratorsList(data);
		}
	}, [searchTerm, data]);

	useEffect(() => {
		dispatch(fetchCollaborators())
			.unwrap()
			.catch(error => {
				showError(error);
			});
		dispatch(fetchFolders());
	}, []);

	const handleAddUser = () => {
		if (!hasActiveSubscription) {
			dispatch(setUpgradeToAddUsersModal(true));
		} else {
			dispatch(setCreateCollaboratorModal(true));
		}
	};

	return (
		<div className="mx-auto flex w-full flex-col gap-12 px-8 md:gap-10">
			<div className="mt-[36px] flex items-center justify-between">
				<h1 className="font-title text-xl font-semibold leading-8 lg:text-[32px] lg:leading-10">
					{t('users.title')}
				</h1>
				<Button onClick={handleAddUser} size={mdDown ? 'sm' : ''}>
					{mdDown ? '+' : t('users.addUser')}
				</Button>
			</div>
			<div>
				<CollaboratorsModal />
				<DeleteCollaboratorModal />
				<UpgradeToAddUsersModal />

				<div className="flex flex-col gap-2">
					<div className="flex flex-col gap-3">
						<h2 className="text-xs font-semibold leading-5">
							{t('users.title')}
						</h2>
						<SearchInput
							name="search"
							className="mb-2 w-[226px]"
							onChange={e => dispatch(setSearchTerm(e.target.value))}
							placeholder={t('myDocuments.search')}
						/>
						<Table data={collaboratorsList} />
					</div>
				</div>
			</div>
		</div>
	);
}

export default Collaborators;
